import { atom, useAtom } from 'jotai';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { trpc, trpcClient } from '@/lib/trpc';
import type { Plan } from '@deepguide-ai/types'
import { getDevPlanOverride } from '@/utils/dev-tools';

// Types for organization and workspace
export interface Organization {
  id: string;
  name: string;
  slug: string;
  logo?: string | null;
  createdAt: string | Date;
  updatedAt: string | Date;
  metadata?: string | null;
  plan?: Plan; // Current subscription plan
}

export interface Workspace {
  id: string;
  slug: string;
  title: string;
  description?: string | null;
  organizationId?: string | null;
  ownerId: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  // Optional mount type for synced workspaces (e.g., GITHUB, NOTION)
  mountType?: string;
  // Workspace type (DOCUMENTATION, COMPLIANCE, etc.)
  type?: string;
}
export interface Tag {
  id: string;
  name: string;
  description?: string | null;
}

// Basic atoms
const activeOrganizationAtom = atom<Organization | null>(null);
const activeWorkspaceAtom = atom<Workspace | null>(null);
export const isLoadingWorkspaceStoreAtom = atom<boolean>(true);

// Helper functions to persist data in localStorage
const persistOrganization = (organization: Organization | null) => {
  if (organization) {
    localStorage.setItem('auth:activeOrg', JSON.stringify(organization));
  }
  return organization;
};

const persistWorkspace = (workspace: Workspace | null) => {
  if (workspace) {
    localStorage.setItem('auth:activeWorkspace', JSON.stringify(workspace));
    localStorage.setItem('auth:activeWorkspaceId', workspace.id);
  }
  return workspace;
};

// Enhanced atoms with localStorage persistence
export const activeOrganizationWithStorageAtom = atom(
  (get) => {
    const activeOrg = get(activeOrganizationAtom);
    if (activeOrg) {
      return activeOrg;
    }
    const storedOrg = localStorage.getItem('auth:activeOrg');
    return storedOrg ? JSON.parse(storedOrg) : null;
  },
  (_get, set, organization: Organization | null) => {
    set(activeOrganizationAtom, persistOrganization(organization));
  }
);

export const activeWorkspaceWithStorageAtom = atom(
  (get) => {
    const activeWs = get(activeWorkspaceAtom);
    if (activeWs) {
      return activeWs;
    }
    const storedWs = localStorage.getItem('auth:activeWorkspace');
    return storedWs ? JSON.parse(storedWs) : null;
  },
  (_get, set, workspace: Workspace | null) => {
    set(activeWorkspaceAtom, persistWorkspace(workspace));
  }
);

/**
 * Derived atom: the full list of frameworks the workspace is enrolled in.
 * Used by UI that conditionally renders framework-specific fields
 * (e.g. HIPAA BAA fields on the Vendor page).
 */
export const enabledFrameworksAtom = atom<string[]>((get) => {
  const workspace = get(activeWorkspaceWithStorageAtom);
  const frameworks = (workspace as any)?.enabledFrameworks;
  return Array.isArray(frameworks) ? frameworks : [];
});

/** True when the current workspace has HIPAA in enabledFrameworks. */
export const hipaaEnabledAtom = atom<boolean>((get) => {
  return get(enabledFrameworksAtom).includes('HIPAA');
});

export const workspaceIdAtom = atom<string>((get) => {
  const workspace = get(activeWorkspaceWithStorageAtom);
  return workspace?.id || '';
});

export const workspaceSlugAtom = atom<string>((get) => {
  const workspace = get(activeWorkspaceWithStorageAtom);
  return workspace?.slug || '';
});

// Stub atoms — removed but still imported by legacy version/branch components
export const activeBranchAtom = atom<any>(null);
export const activeGitStateAtom = atom<any>(null);
export const branchesAtom = atom<any[]>([]);

/**
 * Derived atom that returns the organization with dev plan override applied (dev mode only)
 * Use this instead of activeOrganizationWithStorageAtom when you need plan-aware logic
 */
export const activeOrganizationWithPlanAtom = atom<Organization | null>((get) => {
  const organization = get(activeOrganizationWithStorageAtom);
  if (!organization) return null;

  // Apply dev plan override in development mode
  const devPlanOverride = getDevPlanOverride();
  if (devPlanOverride) {
    return {
      ...organization,
      plan: devPlanOverride,
    };
  }

  return organization;
});


// Hook to initialize store from localStorage
export const useInitializeWorkspaceStore = () => {
  const [, setOrg] = useAtom(activeOrganizationWithStorageAtom);
  const [, setWorkspace] = useAtom(activeWorkspaceWithStorageAtom);
  const [, setIsLoading] = useAtom(isLoadingWorkspaceStoreAtom);
  
  useEffect(() => {
    try {
      // Get organization from localStorage
      const storedOrg = localStorage.getItem('auth:activeOrg');
      if (storedOrg) {
        setOrg(JSON.parse(storedOrg));
      }

      // Get workspace from localStorage
      const storedWorkspace = localStorage.getItem('auth:activeWorkspace');
      if (storedWorkspace) {
        setWorkspace(JSON.parse(storedWorkspace));
      }
    } catch (error) {
      console.error('Error initializing from localStorage:', error);
    } finally {
      setIsLoading(false);
    }
  }, [setOrg, setWorkspace, setIsLoading]);
};

// Hook to clear workspace store data
export const useClearWorkspaceStore = () => {
  const [, setWorkspace] = useAtom(activeWorkspaceWithStorageAtom);
  
  return () => {
    setWorkspace(null);
    localStorage.removeItem('auth:activeWorkspace');
    localStorage.removeItem('auth:activeWorkspaceId');
  };
};

// Hook to update based on route params
export const useUpdateWorkspaceStore = (workspaceId?: string) => {
  const [, setOrg] = useAtom(activeOrganizationWithStorageAtom);
  const [activeWs, setWorkspace] = useAtom(activeWorkspaceWithStorageAtom);
  const [isLoading] = useAtom(isLoadingWorkspaceStoreAtom);

  useEffect(() => {
    // Skip if we're still initializing from localStorage or params are missing
    if (isLoading ) return;
    
    // Skip if we already have matching data
    if (
      activeWs?.id === workspaceId
    ) {
      return;
    }

    const updateStore = async () => {
      try {
        if (workspaceId && workspaceId !== 'undefined' && workspaceId !== 'null') {
          try {
            const workspaceData = await trpcClient.workspaces.getWorkspaceById.query({
              id: workspaceId
            });
            
            if (workspaceData) {
              console.log('Updating active workspace:', workspaceData.title);
              setWorkspace(workspaceData);
              setOrg(workspaceData.organization);
            }
          } catch (error) {
            console.error('Error fetching workspace:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching organization:', error);
      }
    };

    updateStore();
  }, [workspaceId, activeWs?.id, setOrg, setWorkspace, isLoading]);
}; 

// Hook to periodically refresh workspace data using TanStack Query
export const usePeriodicWorkspaceRefresh = () => {
  const [activeOrganization, setOrg] = useAtom(activeOrganizationWithStorageAtom);
  const [, setWorkspace] = useAtom(activeWorkspaceWithStorageAtom);

  // Use TanStack Query with refetchInterval for automatic refreshing
  const { data, error, refetch } = useQuery({
    ...trpc.organizations.getOrganizationWithWorkspaces.queryOptions({}),
    enabled: !!activeOrganization?.id && typeof activeOrganization.id === 'string',
    refetchInterval: 2 * 60 * 1000, // 2 minutes
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000, // Consider data stale after 30 seconds to ensure fresh data
  });

  // Handle data updates with useEffect
  useEffect(() => {
    if (data?.organization && data.organization.id) {
      const organization = data.organization;
      
      try {
        // Update localStorage
        localStorage.setItem('auth:activeOrg', JSON.stringify(organization));
        
        // Update atoms
        setOrg(organization);
        
        // Update current workspace if it exists in the new data
        const storedWorkspaceStr = localStorage.getItem('auth:activeWorkspace');
        if (storedWorkspaceStr) {
          try {
            const currentWorkspace = JSON.parse(storedWorkspaceStr);
            if (currentWorkspace?.id) {
              const updatedWorkspace = organization.workspaces.find((w: any) => w.id === currentWorkspace.id);
              if (updatedWorkspace) {
                localStorage.setItem('auth:activeWorkspace', JSON.stringify(updatedWorkspace));
                setWorkspace(updatedWorkspace);
              }
            }
          } catch (parseError) {
            console.error('Error parsing stored workspace:', parseError);
            // Clear invalid stored workspace
            localStorage.removeItem('auth:activeWorkspace');
          }
        }
        
        console.log('Workspace data refreshed via TanStack Query');
      } catch (error) {
        console.error('Error updating workspace data from refresh:', error);
      }
    }
  }, [data, setOrg, setWorkspace]);

  // Handle errors with useEffect
  useEffect(() => {
    if (error) {
      console.error('Failed to refresh workspace data:', error);
    }
  }, [error]);

  return { data, refetch };
};
